import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import { useTreeViewContext } from "../TreeViewContext";
import {
  procurementNodeId,
  procurementItemNodeId,
  sectionItemNodeId,
} from "../utils/treeNodeIds";
import {
  procurementItemDisplayText,
  procurementItemsForPlant,
} from "../utils/procurementHelpers";

export const ProcurementItemNode = ({ plant, category, item }) => {
  const { nodeTargetRef } = useTreeViewContext();
  const rowId = item?.id ?? item?.mrqId ?? item?.mrqNumber ?? item?.poNumber;
  const id = procurementItemNodeId(plant.id, category.key, rowId);
  nodeTargetRef.current[id] = {
    kind: "procurement",
    plantId: plant.id,
    procurementType: category.key,
    itemId: item.id,
  };

  const Icon = category.icon;
  const displayText = procurementItemDisplayText(category, item, rowId);

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<Icon fontSize="inherit" sx={{ color: "info.main" }} />}
      label={
        <Typography
          variant="caption"
          title={displayText}
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {displayText}
        </Typography>
      }
    />
  );
};

// One row of a listed section - Project Control, Dashboard, AIMS, Client
// Data. Empty for now: no children and no target, so it renders as a plain
// leaf and a click does nothing. Each becomes a link as the page behind it
// arrives (see PLACEHOLDER_SECTIONS).
export const SectionItemNode = ({ plant, sectionKey, item }) => {
  const id = sectionItemNodeId(plant.id, sectionKey, item.key);
  const Icon = item.icon;

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<Icon fontSize="inherit" sx={{ color: "info.main" }} />}
      label={
        <Typography
          variant="body2"
          noWrap
          title={item.label}
          sx={{ minWidth: 0 }}
        >
          {item.label}
        </Typography>
      }
    />
  );
};

export const ProcurementCategoryNode = ({ plant, category }) => {
  const { treeData } = useTreeViewContext();
  const id = procurementNodeId(plant.id, category.key);
  const plantData = treeData.mcsTreeByPlant[plant.id];
  const items = procurementItemsForPlant(plantData, category);
  const Icon = category.icon;

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<Icon fontSize="inherit" sx={{ color: "info.main" }} />}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {category.label}
          </Typography>
          {plantData && plantData !== "loading" && (
            <Typography variant="caption" color="text.disabled">
              ({items.length})
            </Typography>
          )}
        </Box>
      }
    >
      {plantData === "loading" ? (
        <TreeItem
          key={`${id}-loading`}
          nodeId={`${id}-loading`}
          label={
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
            >
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          }
        />
      ) : items.length > 0 ? (
        items.map((item) => (
          <ProcurementItemNode
            key={item?.id ?? item?.mrqId ?? item?.mrqNumber ?? item?.poNumber}
            plant={plant}
            category={category}
            item={item}
          />
        ))
      ) : (
        <TreeItem
          key={`${id}-empty`}
          nodeId={`${id}-empty`}
          label={
            <Typography variant="caption" color="text.secondary">
              No items
            </Typography>
          }
        />
      )}
    </TreeItem>
  );
};
